"use client";

import { fetchFile } from "@ffmpeg/util";
import {
	AlertTriangle,
	Download,
	Loader,
	Upload,
	Video,
	Play,
	X,
	GripVertical,
	Combine,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { FileDropZone } from "@/components/FileDropZone";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import VideoPlayer from "@/components/VideoPlayer";
import { useFFmpeg } from "@/contexts/FFmpegContext";
import { useAccessibility } from "@/hooks/useAccessibility";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import logger from "@/lib/logger";
import { useTranslations } from "next-intl";

// Output format options for video stitching
const OUTPUT_FORMATS = [
	{
		label: "MP4 (H.264) - Universal Compatibility",
		ext: "mp4",
		mime: "video/mp4",
		icon: Video,
		command: ["-c:v", "libx264", "-crf", "23", "-preset", "medium", "-c:a", "aac", "-b:a", "128k"],
		description: "Best compatibility across all devices"
	},
	{
		label: "MP4 (H.265/HEVC) - Better Compression",
		ext: "mp4",
		mime: "video/mp4",
		icon: Video,
		command: ["-c:v", "libx265", "-crf", "28", "-preset", "medium", "-c:a", "aac", "-b:a", "128k"],
		description: "Smaller file size, newer codec"
	},
	{
		label: "WebM (VP9) - Web Optimized",
		ext: "webm",
		mime: "video/webm",
		icon: Video,
		command: ["-c:v", "libvpx-vp9", "-crf", "30", "-b:v", "0", "-c:a", "libopus", "-b:a", "128k"],
		description: "Optimized for web streaming"
	},
	{
		label: "MOV (QuickTime) - High Quality",
		ext: "mov",
		mime: "video/quicktime",
		icon: Video,
		command: ["-c:v", "libx264", "-crf", "18", "-preset", "slow", "-c:a", "aac", "-b:a", "192k"],
		description: "High quality for professional use"
	}
];

interface VideoFile {
	id: string;
	file: File;
	url: string;
	name: string;
	size: number;
	duration?: number;
}

const VideoStitcher = () => {
	const t = useTranslations("videoStitcher");
	const { ffmpegInstance, isLoaded, isLoading, hardwareInfo, error, loadFFmpeg } = useFFmpeg();

	useEffect(() => {
		loadFFmpeg();
	}, [loadFFmpeg]);

	const { announce, announceProgress, announceError, announceSuccess, focusElement } = useAccessibility();
	
	const [videoFiles, setVideoFiles] = useState<VideoFile[]>([]);
	const [selectedFormat, setSelectedFormat] = useState(OUTPUT_FORMATS[0]);
	const [outputURL, setOutputURL] = useState("");
	const [isProcessing, setIsProcessing] = useState(false);
	const [statusLog, setStatusLog] = useState(t("status.initializing"));
	const [progress, setProgress] = useState(0);
	const [startTime, setStartTime] = useState<number | null>(null);
	const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState<string>("");
	const [totalInputSize, setTotalInputSize] = useState<number>(0);
	const [outputFileSize, setOutputFileSize] = useState<number>(0);
	const [draggedItem, setDraggedItem] = useState<string | null>(null);
	const [dragOverItem, setDragOverItem] = useState<string | null>(null);


	// Update status based on FFmpeg loading state
	useEffect(() => {
		if (error) {
			setStatusLog(error);
		} else if (isLoading) {
			setStatusLog(t("status.loading"));
		} else if (isLoaded) {
			setStatusLog(t("status.ready"));
		} else {
			setStatusLog(t("status.initializing"));
		}
	}, [error, isLoading, isLoaded]);

	// Progress tracking state
	const [currentPhase, setCurrentPhase] = useState<'analysis' | 'normalization' | 'concatenation'>('analysis');
	const [currentFileProcessing, setCurrentFileProcessing] = useState<string>('');

	// Set up FFmpeg progress and log listeners
	useEffect(() => {
		if (ffmpegInstance && isProcessing) {
			const handleProgress = ({ progress }: { progress: number }) => {
				// Clamp progress between 0 and 1 to prevent overflow
				const clampedProgress = Math.min(Math.max(progress || 0, 0), 1);
				
				// Calculate overall progress based on current phase
				// Analysis: 0-15%, Normalization: 15-70%, Concatenation: 70-100%
				let overallProgress = 0;
				switch (currentPhase) {
					case 'analysis':
						overallProgress = clampedProgress * 0.15;
						break;
					case 'normalization':
						overallProgress = 0.15 + (clampedProgress * 0.55);
						break;
					case 'concatenation':
						overallProgress = 0.70 + (clampedProgress * 0.30);
						break;
				}
				
				setProgress(overallProgress);
				
				if (startTime && overallProgress > 0) {
					const elapsed = Date.now() - startTime;
					const totalEstimated = elapsed / overallProgress;
					const remaining = totalEstimated - elapsed;

					if (remaining > 0) {
						const minutes = Math.floor(remaining / 60000);
						const seconds = Math.floor((remaining % 60000) / 1000);
						const timeString = minutes > 0 ? t("status.timeRemainingMinutes", { minutes, seconds }) : t("status.timeRemainingSeconds", { seconds });
						setEstimatedTimeRemaining(timeString);
					} else {
						setEstimatedTimeRemaining(t("status.almostDone"));
					}
				}
				announceProgress(Math.round(overallProgress * 100), 100, t("a11y.progressDescription"));
			};

			// Enhanced log handler for error detection
			const handleLog = ({ message }: { message: string }) => {
				// Parse ffmpeg logs for critical errors that should stop processing
				const lowerMessage = message.toLowerCase();
				
				// Critical errors that should cause immediate failure
				const criticalErrors = [
					'no such file or directory',
					'invalid data found when processing input',
					'could not find codec',
					'decoder not found',
					'invalid argument',
					'permission denied',
					'disk full',
					'out of memory',
					'corrupted',
					'not supported',
					'unknown encoder',
					'invalid stream',
					'conversion failed'
				];
				
				// Check for critical errors
				for (const error of criticalErrors) {
					if (lowerMessage.includes(error)) {
						logger.error({ message }, 'FFmpeg Critical Error');
	// Store the error for the main process to handle
						(window as any).__ffmpegCriticalError = message;
						return;
					}
				}
				
				// Log warnings and info for debugging
				if (lowerMessage.includes('warning') || lowerMessage.includes('deprecated')) {
					logger.warn({ message }, 'FFmpeg Warning');
				} else {
					logger.debug({ message }, 'FFmpeg Log');
				}
			};

			ffmpegInstance.on("progress", handleProgress);
			ffmpegInstance.on("log", handleLog);
			
			return () => {
				ffmpegInstance.off("progress", handleProgress);
				ffmpegInstance.off("log", handleLog);
			};
		}
	}, [ffmpegInstance, isProcessing, startTime, announceProgress, currentPhase]);

	// Handle file selection (multiple files)
	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(event.target.files || []);
		if (files.length === 0) return;

		const newVideoFiles: VideoFile[] = files.map((file, index) => {
			// Validate file type
			if (!file.type.startsWith('video/')) {
				throw new Error(`${file.name} is not a video file`);
			}

			return {
				id: `video-${Date.now()}-${index}`,
				file,
				url: URL.createObjectURL(file),
				name: file.name,
				size: file.size
			};
		});

		setVideoFiles(prev => [...prev, ...newVideoFiles]);
		setTotalInputSize(prev => prev + files.reduce((acc, file) => acc + file.size, 0));
		setOutputURL("");
		setOutputFileSize(0);
		
		setStatusLog(t("status.filesAdded", { count: files.length, total: videoFiles.length + files.length }));
		announce(t("a11y.filesAdded", { count: files.length }));
	};

	// Remove a video file
	const removeVideoFile = (id: string) => {
		setVideoFiles(prev => {
			const fileToRemove = prev.find(f => f.id === id);
			if (fileToRemove) {
				URL.revokeObjectURL(fileToRemove.url);
				setTotalInputSize(prevSize => prevSize - fileToRemove.size);
			}
			return prev.filter(f => f.id !== id);
		});
		setOutputURL("");
		setOutputFileSize(0);
		announce(t("status.fileRemoved"));
	};

	// Drag and drop handlers
	const handleDragStart = (e: React.DragEvent, id: string) => {
		setDraggedItem(id);
		e.dataTransfer.effectAllowed = 'move';
		e.dataTransfer.setData('text/plain', id);
	};

	const handleDragOver = (e: React.DragEvent, id: string) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = 'move';
		setDragOverItem(id);
	};

	const handleDragLeave = () => {
		setDragOverItem(null);
	};

	const handleDrop = (e: React.DragEvent, targetId: string) => {
		e.preventDefault();
		const draggedId = e.dataTransfer.getData('text/plain');
		
		if (draggedId === targetId) {
			setDraggedItem(null);
			setDragOverItem(null);
			return;
		}

		setVideoFiles(prev => {
			const draggedIndex = prev.findIndex(f => f.id === draggedId);
			const targetIndex = prev.findIndex(f => f.id === targetId);
			
			if (draggedIndex === -1 || targetIndex === -1) return prev;

			const newFiles = [...prev];
			const [draggedFile] = newFiles.splice(draggedIndex, 1);
			newFiles.splice(targetIndex, 0, draggedFile);
			
			return newFiles;
		});

		setDraggedItem(null);
		setDragOverItem(null);
		announce(t("status.fileReordered"));
	};

	const handleDragEnd = () => {
		setDraggedItem(null);
		setDragOverItem(null);
	};

	// Core stitching logic
	const stitchVideos = useCallback(async () => {
		if (videoFiles.length < 2 || !ffmpegInstance) {
			setStatusLog(t("status.needAtLeast2"));
			return;
		}

		setIsProcessing(true);
		setOutputURL("");
		setProgress(0);
		setCurrentPhase('analysis');
		setStartTime(Date.now());
		setEstimatedTimeRemaining("");
		setStatusLog(t("status.starting"));

		announce(t("a11y.stitchingStarted"), "polite");

		const outputFileName = `stitched.${selectedFormat.ext}`;

		try {
			// Check file size limits for mobile devices
			const totalSizeMB = totalInputSize / (1024 * 1024);
			const memoryLimitMB = hardwareInfo.memory * 1024;

			if (hardwareInfo.isLowEnd && totalSizeMB > memoryLimitMB * 0.2) {
				throw new Error(t("errors.fileTooLarge", { size: totalSizeMB.toFixed(1) }));
			}

			setStatusLog(t("status.loadingFiles"));

			// Write all video files to FFmpeg filesystem
			const inputFileNames: string[] = [];
			for (let i = 0; i < videoFiles.length; i++) {
				const inputFileName = `input${i}.${videoFiles[i].file.name.split(".").pop()}`;
				inputFileNames.push(inputFileName);
				await ffmpegInstance.writeFile(inputFileName, await fetchFile(videoFiles[i].file));
			}

			setStatusLog(t("status.analyzingProbe"));

			// Enhanced video metadata interface
			interface VideoInfo {
				duration: number;
				width: number;
				height: number;
				fps: number;
				hasAudio: boolean;
				audioChannels: number;
				videoCodec: string;
				audioCodec: string;
				containerFormat: string;
				bitrate: number;
				aspectRatio: number;
				isHighFrameRate: boolean;
				needsNormalization: boolean;
			}

			const videoInfos: VideoInfo[] = [];
			
			// Clear any previous critical errors
			(window as any).__ffmpegCriticalError = null;
			
			for (let i = 0; i < inputFileNames.length; i++) {
				try {
					setStatusLog(t("status.analyzingVideo", { current: i + 1, total: inputFileNames.length }));
					setCurrentFileProcessing(videoFiles[i].name);
					
					// Update analysis progress (0-15% of total)
					const analysisProgress = (i / inputFileNames.length) * 0.15;
					setProgress(analysisProgress);
					
					// Check for critical errors during analysis
					if ((window as any).__ffmpegCriticalError) {
						throw new Error(`FFmpeg error: ${(window as any).__ffmpegCriticalError}`);
					}
					
					const file = videoFiles[i];
					
					// Use HTML5 video element for reliable metadata extraction
					const tempVideo = document.createElement('video');
					tempVideo.src = URL.createObjectURL(file.file);
					
					await new Promise((resolve, reject) => {
						tempVideo.addEventListener('loadedmetadata', resolve, { once: true });
						tempVideo.addEventListener('error', reject, { once: true });
						tempVideo.load();
					});
					
					// Validate metadata
					const duration = tempVideo.duration;
					const width = tempVideo.videoWidth;
					const height = tempVideo.videoHeight;
					
					if (!duration || duration <= 0) {
						throw new Error(t("errors.invalidDuration", { name: videoFiles[i].name }));
					}

					if (!width || width <= 0 || !height || height <= 0) {
						throw new Error(t("errors.invalidResolution", { name: videoFiles[i].name }));
					}

					if (duration > 3600) { // More than 1 hour
						throw new Error(t("errors.videoTooLong", { name: videoFiles[i].name, minutes: Math.round(duration/60) }));
					}
					
					// Enhanced audio detection
					let hasAudio = false;
					let audioChannels = 0;
					
					// Try multiple detection methods for audio with proper typing
					const videoElement = tempVideo as HTMLVideoElement & {
						mozHasAudio?: boolean;
						webkitAudioDecodedByteCount?: number;
						audioTracks?: { length: number };
					};
					
					if (videoElement.mozHasAudio) {
						hasAudio = true;
						audioChannels = 2; // Default stereo assumption
					} else if (videoElement.webkitAudioDecodedByteCount && videoElement.webkitAudioDecodedByteCount > 0) {
						hasAudio = true;
						audioChannels = 2;
					} else if (videoElement.audioTracks && videoElement.audioTracks.length > 0) {
						hasAudio = true;
						audioChannels = 2;
					}
					
					// Try AudioContext for more precise detection (if available)
					try {
						const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
						if (AudioContextClass) {
							const audioContext = new AudioContextClass();
							const source = audioContext.createMediaElementSource(tempVideo);
							if (source && source.channelCount) {
								hasAudio = true;
								audioChannels = source.channelCount;
							}
							audioContext.close();
						}
					} catch {
						// AudioContext not available or failed
					}
					
					// Determine file format and codecs from file extension and MIME type
					const fileExtension = file.file.name.split('.').pop()?.toLowerCase() || '';
					const mimeType = file.file.type;
					
					let videoCodec = 'unknown';
					let audioCodec = 'unknown';
					let containerFormat = fileExtension;
					
					// Enhanced format detection
					switch (fileExtension) {
						case 'mp4':
						case 'm4v':
							videoCodec = 'h264';
							audioCodec = hasAudio ? 'aac' : 'none';
							containerFormat = 'mp4';
							break;
						case 'mov':
							videoCodec = 'h264';
							audioCodec = hasAudio ? 'aac' : 'none';
							containerFormat = 'mov';
							break;
						case 'avi':
							videoCodec = 'h264';
							audioCodec = hasAudio ? 'mp3' : 'none';
							containerFormat = 'avi';
							break;
						case 'mkv':
							videoCodec = 'h264';
							audioCodec = hasAudio ? 'aac' : 'none';
							containerFormat = 'matroska';
							break;
						case 'webm':
							videoCodec = 'vp8';
							audioCodec = hasAudio ? 'vorbis' : 'none';
							containerFormat = 'webm';
							break;
						case 'flv':
							videoCodec = 'h264';
							audioCodec = hasAudio ? 'aac' : 'none';
							containerFormat = 'flv';
							break;
						default:
							// Try to infer from MIME type
							if (mimeType.includes('mp4')) {
								videoCodec = 'h264';
								audioCodec = hasAudio ? 'aac' : 'none';
								containerFormat = 'mp4';
							}
					}
					
					// Estimate bitrate
					const bitrate = Math.round((file.file.size * 8) / duration);
					
					// Calculate aspect ratio
					const aspectRatio = width / height;
					
					// Estimate frame rate using a more sophisticated approach
					let estimatedFps = 30; // Default
					
					// Try to detect frame rate by analyzing video characteristics
					// High bitrate + high resolution often indicates higher frame rate
					const pixelsPerSecond = width * height;
					const bitratePerPixel = bitrate / pixelsPerSecond;
					
					// Heuristic frame rate detection
					if (bitratePerPixel > 0.1 && (width >= 1920 || height >= 1080)) {
						// Likely high frame rate content
						estimatedFps = 60;
					} else if (containerFormat === 'webm' || fileExtension === 'mov') {
						// These formats often have variable frame rates
						estimatedFps = bitrate > 5000000 ? 60 : 30;
					} else if (fileExtension === 'flv' || containerFormat === 'avi') {
						// Older formats typically 30fps or lower
						estimatedFps = 30;
					}
					
					const isHighFrameRate = estimatedFps >= 50;
					
					videoInfos.push({
						duration,
						width,
						height,
						fps: estimatedFps,
						hasAudio,
						audioChannels: hasAudio ? audioChannels : 0,
						videoCodec,
						audioCodec,
						containerFormat,
						bitrate,
						aspectRatio,
						isHighFrameRate,
						needsNormalization: false // Will be determined later
					});
					
					URL.revokeObjectURL(tempVideo.src);
					
				} catch (error) {
					const fileName = videoFiles[i].name;
					const errorMessage = t("errors.cannotAnalyze", { name: fileName, error: error instanceof Error ? error.message : 'Unknown error occurred' });
					
					setStatusLog(errorMessage);
					announceError(errorMessage);
					throw new Error(errorMessage);
				}
			}
			
			// Analysis phase complete
			setProgress(0.15);
			setCurrentPhase('normalization');
			setCurrentFileProcessing('');
			
			// Determine optimal processing strategy
			const hasHighFrameRateVideo = videoInfos.some(info => info.isHighFrameRate);
			const targetFps = hasHighFrameRateVideo ? 60 : 30;
			
			// Check format compatibility for optimization
			const firstVideo = videoInfos[0];
			let needsNormalization = false;
			
			// Determine if normalization is needed
			for (let i = 1; i < videoInfos.length; i++) {
				const current = videoInfos[i];
				
				// Check for incompatibilities that require normalization
				if (
					current.videoCodec !== firstVideo.videoCodec ||
					current.audioCodec !== firstVideo.audioCodec ||
					current.containerFormat !== firstVideo.containerFormat ||
					current.width !== firstVideo.width ||
					current.height !== firstVideo.height ||
					current.fps !== targetFps ||
					current.audioChannels !== firstVideo.audioChannels ||
					Math.abs(current.aspectRatio - firstVideo.aspectRatio) > 0.01
				) {
					needsNormalization = true;
					break;
				}
			}
			
			// Also check if first video needs normalization to target specs
			if (firstVideo.fps !== targetFps) {
				needsNormalization = true;
			}
			
			// Mark videos that need normalization
			videoInfos.forEach(info => {
				info.needsNormalization = needsNormalization || info.fps !== targetFps;
			});
			
			// Find the most common resolution for target
			const resolutions = videoInfos.map(info => `${info.width}x${info.height}`);
			const resolutionCounts = resolutions.reduce((acc, res) => {
				acc[res] = (acc[res] || 0) + 1;
				return acc;
			}, {} as Record<string, number>);
			
			const mostCommonResolution = Object.entries(resolutionCounts)
				.sort(([,a], [,b]) => b - a)[0][0];
			const [targetWidth, targetHeight] = mostCommonResolution.split('x').map(Number);
			
			// Calculate total expected duration for progress tracking
			const totalDuration = videoInfos.reduce((sum, info) => sum + info.duration, 0);
			
			const strategyMessage = needsNormalization
				? t("status.strategyNormalize", { width: targetWidth, height: targetHeight, fps: targetFps })
				: t("status.strategyDirect", { fps: targetFps });

			setStatusLog(t("status.analysisComplete", { strategy: strategyMessage, duration: Math.round(totalDuration) }));
			
			// Optimize for hardware capabilities
			let threadCount = hardwareInfo.cores;
			if (hardwareInfo.isLowEnd) {
				threadCount = 1;
			} else if (!hardwareInfo.supportsSharedArrayBuffer) {
				threadCount = Math.max(1, Math.floor(threadCount * 0.5));
			}

			// Phase 1: Normalization (if needed)
			const normalizedFileNames: string[] = [];
			
			if (needsNormalization) {
				setStatusLog(t("status.startingNormalization", { count: videoInfos.length, width: targetWidth, height: targetHeight, fps: targetFps }));
				
				for (let i = 0; i < inputFileNames.length; i++) {
					// Check for critical errors
					if ((window as any).__ffmpegCriticalError) {
						throw new Error(`FFmpeg error during normalization: ${(window as any).__ffmpegCriticalError}`);
					}
					
					const info = videoInfos[i];
					const normalizedFileName = `normalized_${i}.mp4`;
					normalizedFileNames.push(normalizedFileName);
					
					setStatusLog(t("status.normalizingVideo", { current: i + 1, total: inputFileNames.length, name: videoFiles[i].name }));
					setCurrentFileProcessing(videoFiles[i].name);
					
					// Build normalization command based on video characteristics
					const normalizationArgs: string[] = [
						"-i", inputFileNames[i]
					];
					
					// Video processing chain
					const videoFilters: string[] = [];
					
					// Scale to target resolution if needed
					if (info.width !== targetWidth || info.height !== targetHeight) {
						videoFilters.push(`scale=${targetWidth}:${targetHeight}:force_original_aspect_ratio=decrease`);
						videoFilters.push(`pad=${targetWidth}:${targetHeight}:(ow-iw)/2:(oh-ih)/2:black`);
					}
					
					// Set aspect ratio and frame rate
					videoFilters.push("setsar=1");
					
					// Frame rate processing with interpolation for high frame rate content
					if (info.fps !== targetFps) {
						if (targetFps === 60 && info.fps < 50) {
							// Use frame interpolation for smooth 60fps
							videoFilters.push(`minterpolate=fps=${targetFps}:mi_mode=mci:mc_mode=aobmc:vsbmc=1`);
						} else {
							// Simple frame rate conversion
							videoFilters.push(`fps=${targetFps}`);
						}
					} else {
						videoFilters.push(`fps=${targetFps}`);
					}
					
					// Timestamp normalization
					videoFilters.push("setpts=PTS-STARTPTS");
					
					// Apply video filters
					if (videoFilters.length > 0) {
						normalizationArgs.push("-filter:v", videoFilters.join(','));
					}
					
					// Audio processing - always ensure audio track exists
					const audioFilters: string[] = [];
					
					if (info.hasAudio) {
						// Process existing audio
						audioFilters.push("aresample=48000");
						
						// Handle different channel configurations
						if (info.audioChannels === 1) {
							// Convert mono to stereo
							audioFilters.push("aformat=channel_layouts=stereo");
						} else if (info.audioChannels > 2) {
							// Downmix multichannel to stereo
							audioFilters.push("aformat=channel_layouts=stereo");
						} else {
							// Ensure stereo format
							audioFilters.push("aformat=channel_layouts=stereo");
						}
						
						// Timestamp normalization for audio
						audioFilters.push("asetpts=PTS-STARTPTS");
						
						normalizationArgs.push("-filter:a", audioFilters.join(','));
					} else {
						// Generate silent audio track using anullsrc filter
						audioFilters.push("anullsrc=channel_layout=stereo:sample_rate=48000");
						audioFilters.push("asetpts=PTS-STARTPTS");
						
						normalizationArgs.push("-filter:a", audioFilters.join(','));
					}
					
					// Always add audio encoding parameters
					normalizationArgs.push("-c:a", "aac", "-b:a", "128k", "-ar", "48000", "-ac", "2");
					
					// Video encoding settings
					normalizationArgs.push(
						"-c:v", "libx264",
						"-preset", hardwareInfo.isLowEnd ? "ultrafast" : "medium",
						"-crf", "23",
						"-pix_fmt", "yuv420p",
						"-threads", threadCount.toString(),
						"-avoid_negative_ts", "make_zero",
						"-fflags", "+genpts+igndts",
						"-fps_mode", "cfr",
						"-video_track_timescale", "90000",
						normalizedFileName
					);
					
					// Execute normalization
					logger.debug({ commandIndex: i + 1, normalizationArgs }, 'Normalization command');
					
					await ffmpegInstance.exec(normalizationArgs);
					
					// Update progress (15% + (i / total) * 55%)
					const normalizationProgress = 0.15 + ((i + 1) / inputFileNames.length) * 0.55;
					setProgress(normalizationProgress);
				}
				
				setStatusLog(t("status.normalizationComplete"));
				setProgress(0.70);
				setCurrentPhase('concatenation');
				setCurrentFileProcessing('');
				
			} else {
				// Skip normalization - use original files
				for (let i = 0; i < inputFileNames.length; i++) {
					normalizedFileNames.push(inputFileNames[i]);
				}
				setStatusLog(t("status.skippingNormalization"));
				setProgress(0.70);
				setCurrentPhase('concatenation');
			}

			// Phase 2: Concatenation
			// Check for critical errors before concatenation
			if ((window as any).__ffmpegCriticalError) {
				throw new Error(`FFmpeg error before concatenation: ${(window as any).__ffmpegCriticalError}`);
			}
			
			// Choose concatenation strategy based on format compatibility
			let concatenationStrategy: 'demuxer' | 'filter' = 'demuxer';
			
			if (needsNormalization) {
				// After normalization, all files are identical format - use efficient demuxer
				concatenationStrategy = 'demuxer';
				setStatusLog(t("status.usingDemuxer"));
				
				// Create concat list file
				const concatListContent = normalizedFileNames.map(name => `file '${name}'`).join('\n');
				await ffmpegInstance.writeFile('concat_list.txt', new TextEncoder().encode(concatListContent));
				
				const demuxerCommand = [
					"-f", "concat",
					"-safe", "0",
					"-i", "concat_list.txt",
					"-c", "copy", // No re-encoding needed
					"-movflags", "+faststart",
					"-avoid_negative_ts", "make_zero",
					outputFileName
				];
				
				logger.debug({ demuxerCommand }, 'Demuxer concatenation command');
				logger.debug({ concatListContent }, 'Concat list content');
				
				await ffmpegInstance.exec(demuxerCommand);
				
			} else {
				// Mixed formats or frame rate differences - use filter concatenation
				concatenationStrategy = 'filter';
				setStatusLog(t("status.usingFilter"));
				
				const inputArgs: string[] = [];
				const filterComplex: string[] = [];
				
				// Add input files
				for (let i = 0; i < normalizedFileNames.length; i++) {
					inputArgs.push("-i", normalizedFileNames[i]);
				}
				
				// Process videos with optimized filters
				for (let i = 0; i < normalizedFileNames.length; i++) {
					const info = videoInfos[i];
					
					// Video processing chain
					const videoFilters: string[] = [];
					
					// Scale if needed
					if (info.width !== targetWidth || info.height !== targetHeight) {
						videoFilters.push(`scale=${targetWidth}:${targetHeight}:force_original_aspect_ratio=decrease`);
						videoFilters.push(`pad=${targetWidth}:${targetHeight}:(ow-iw)/2:(oh-ih)/2:black`);
					}
					
					// Ensure consistent frame rate
					videoFilters.push("setsar=1");
					videoFilters.push(`fps=${targetFps}`);
					videoFilters.push("setpts=PTS-STARTPTS");
					
					if (videoFilters.length > 0) {
						filterComplex.push(`[${i}:v]${videoFilters.join(',')}[v${i}]`);
					} else {
						filterComplex.push(`[${i}:v]setpts=PTS-STARTPTS[v${i}]`);
					}
					
					// Audio processing - always ensure audio track exists
					const audioFilters: string[] = [];
					
					if (info.hasAudio) {
						// Process existing audio
						audioFilters.push("aresample=48000");
						
						// Handle channel layout
						if (info.audioChannels === 1) {
							audioFilters.push("aformat=channel_layouts=stereo");
						} else if (info.audioChannels > 2) {
							audioFilters.push("aformat=channel_layouts=stereo");
						} else {
							audioFilters.push("aformat=channel_layouts=stereo");
						}
						
						audioFilters.push("asetpts=PTS-STARTPTS");
						filterComplex.push(`[${i}:a]${audioFilters.join(',')}[a${i}]`);
					} else {
						// Generate silent audio for videos without audio using anullsrc
						audioFilters.push("anullsrc=channel_layout=stereo:sample_rate=48000");
						audioFilters.push("asetpts=PTS-STARTPTS");
						filterComplex.push(`anullsrc=channel_layout=stereo:sample_rate=48000,asetpts=PTS-STARTPTS[a${i}]`);
					}
				}
				
				// Concatenate all streams
				const concatInputs = normalizedFileNames.map((_, i) => `[v${i}][a${i}]`).join('');
				filterComplex.push(`${concatInputs}concat=n=${normalizedFileNames.length}:v=1:a=1[outv][outa]`);

				const filterCommand = [
					...inputArgs,
					"-filter_complex", filterComplex.join(';'),
					"-map", "[outv]",
					"-map", "[outa]",
					...selectedFormat.command,
					"-threads", threadCount.toString(),
					"-movflags", "+faststart",
					"-avoid_negative_ts", "make_zero",
					"-fflags", "+genpts+igndts",
					"-max_muxing_queue_size", "1024",
					"-fps_mode", "cfr",
					"-video_track_timescale", "90000",
					outputFileName
				];

				logger.debug({ filterCommand }, 'Filter concatenation command');
				logger.debug({ concatenationStrategy }, 'Using strategy');

				await ffmpegInstance.exec(filterCommand);
			}
			
			setStatusLog(t("status.concatenationComplete"));

			setStatusLog(t("status.readingFile"));

			// Read output file
			const data = await ffmpegInstance.readFile(outputFileName) as Uint8Array;
			
			// Create downloadable blob
			const blob = new Blob([data as BlobPart], { type: selectedFormat.mime });
			const url = URL.createObjectURL(blob);
			
			setOutputURL(url);
			setOutputFileSize(blob.size);
			// Ensure we reach 100% completion
			setProgress(1);
			setEstimatedTimeRemaining("");

			const sizeChangeMB = (blob.size - totalInputSize) / (1024 * 1024);
			const sizeChangeText = sizeChangeMB > 0
				? t("status.sizeIncreased", { size: Math.abs(sizeChangeMB).toFixed(1) })
				: t("status.sizeReduced", { size: Math.abs(sizeChangeMB).toFixed(1) });

			setStatusLog(t("status.complete", { sizeChange: sizeChangeText }));

			announceSuccess(t("a11y.stitchingComplete", { count: videoFiles.length }));
			setTimeout(() => {
				focusElement("[data-download-button]", 500);
			}, 1000);

		} catch (error) {
			let errorMessage = "Unknown error occurred";
			
			if (error instanceof Error) {
				errorMessage = error.message;
			} else if (typeof error === 'string') {
				errorMessage = error;
			}
			
			setStatusLog(t("status.failed", { error: errorMessage }));
			setProgress(0);
			setCurrentPhase('analysis');
			setEstimatedTimeRemaining("");
			announceError(errorMessage);

		} finally {
			setIsProcessing(false);
			setCurrentFileProcessing('');
			// Cleanup filesystem
			try {
				await ffmpegInstance.deleteFile(outputFileName);
				await ffmpegInstance.deleteFile('concat_list.txt');
				
				// Clean up input files
				for (let i = 0; i < videoFiles.length; i++) {
					const inputFileName = `input${i}.${videoFiles[i].file.name.split(".").pop()}`;
					await ffmpegInstance.deleteFile(inputFileName);
				}
				
				// Clean up normalized files if they exist
				for (let i = 0; i < videoFiles.length; i++) {
					const normalizedFileName = `normalized_${i}.mp4`;
					await ffmpegInstance.deleteFile(normalizedFileName);
				}
			} catch {
				// Ignore cleanup errors
			}
		}
	}, [
		videoFiles,
		ffmpegInstance,
		selectedFormat,
		hardwareInfo,
		totalInputSize,
		announce,
		announceSuccess,
		announceError,
		focusElement,
		t
	]);

	const FormatIcon = selectedFormat.icon;

	return (
		<ErrorBoundary>
			<div className="max-w-7xl mx-auto min-h-screen p-4 sm:p-8">
				<div className="mx-auto p-2 sm:p-6">
					<header className="text-center mb-10">
						<h1 className="text-3xl sm:text-4xl font-black flex items-center justify-center uppercase tracking-wide">
							<Combine className="h-8 w-8 me-4 text-primary" aria-hidden="true" />
							{t("title")}
						</h1>
						<p className="mt-4 text-muted-foreground text-lg font-bold">
							{t("subtitle")}
						</p>
					</header>

					{/* Status Bar */}
					<Alert className={`mb-6 ${isLoaded ? "border-green-500 bg-green-50" : "border-yellow-500 bg-yellow-50"}`}>
						<AlertTriangle className="h-4 w-4" aria-hidden="true" />
						<AlertDescription className="font-bold">{statusLog}</AlertDescription>
					</Alert>

					{/* Main Interface */}
					<div className="flex flex-col gap-6">
						{/* 1. Upload Section */}
						<Card className="p-3 sm:p-6">
							<CardHeader className="p-0 mb-4">
								<CardTitle className="flex items-center gap-3">
									<Upload className="w-6 h-6" aria-hidden="true" />
									{t("steps.selectVideos")}
								</CardTitle>
							</CardHeader>
							<CardContent className="p-0">
								<FileDropZone accept="video/*" multiple disabled={!isLoaded || isProcessing} onFiles={(files) => handleFileChange({ target: { files } } as any)} label={t("fileInput.dropLabel")} activeLabel={t("fileInput.dropActiveLabel")} />
								<p className="mt-2 text-sm text-muted-foreground">
									{t("fileInput.hint")}
								</p>
								
								{/* Video Files List */}
								{videoFiles.length > 0 && (
									<div className="mt-4 space-y-3">
										<h4 className="font-semibold">{t("fileInput.videosToStitch", { count: videoFiles.length })}</h4>
										<p className="text-sm text-muted-foreground">
											{t("fileInput.dragReorder")}
										</p>
										{videoFiles.map((video, index) => (
											<div
												key={video.id}
												draggable={!isProcessing}
												onDragStart={(e) => handleDragStart(e, video.id)}
												onDragOver={(e) => handleDragOver(e, video.id)}
												onDragLeave={handleDragLeave}
												onDrop={(e) => handleDrop(e, video.id)}
												onDragEnd={handleDragEnd}
												className={`flex items-center gap-3 p-3 border rounded-lg cursor-move transition-all ${
													draggedItem === video.id ? 'opacity-50 scale-95' : ''
												} ${
													dragOverItem === video.id ? 'border-primary bg-primary/10' : ''
												} ${
													!isProcessing ? 'hover:border-primary/50' : 'cursor-not-allowed'
												}`}
											>
												<GripVertical className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
												<Badge variant="secondary" className="text-xs">
													{index + 1}
												</Badge>
												<div className="flex-1 min-w-0">
													<p className="font-medium truncate">{video.name}</p>
													<p className="text-sm text-muted-foreground">
														{(video.size / 1024 / 1024).toFixed(2)} MB
													</p>
												</div>
												<Button
													variant="ghost"
													size="sm"
													onClick={() => removeVideoFile(video.id)}
													disabled={isProcessing}
													aria-label={t("fileInput.removeVideo", { name: video.name })}
												>
													<X className="w-4 h-4 text-destructive" aria-hidden="true" />
												</Button>
											</div>
										))}
										<div className="text-sm text-muted-foreground">
											{t("fileInput.totalSize", { size: (totalInputSize / 1024 / 1024).toFixed(2) })}
										</div>
									</div>
								)}
							</CardContent>
						</Card>

						{/* 2. Output Format Selection */}
						{videoFiles.length > 0 && (
							<Card className="p-3 sm:p-6">
								<CardHeader className="p-0 mb-4">
									<CardTitle className="flex items-center gap-3">
										<FormatIcon className="w-6 h-6" aria-hidden="true" />
										{t("steps.selectFormat")}
									</CardTitle>
								</CardHeader>
								<CardContent className="p-0">
									<Select
										value={OUTPUT_FORMATS.indexOf(selectedFormat).toString()}
										onValueChange={(value) => {
											const formatIndex = parseInt(value);
											const format = OUTPUT_FORMATS[formatIndex];
											if (format) {
												setSelectedFormat(format);
												setOutputURL("");
												setStatusLog(t("status.formatChanged", { label: format.label }));
											}
										}}
										disabled={!isLoaded || isProcessing}
									>
										<SelectTrigger className="w-full">
											<SelectValue placeholder={t("formats.placeholder")} />
										</SelectTrigger>
										<SelectContent>
											{OUTPUT_FORMATS.map((format, idx) => (
												<SelectItem key={idx} value={idx.toString()}>
													<div className="flex flex-col">
														<span>{format.label}</span>
														<span className="text-xs text-muted-foreground">{format.description}</span>
													</div>
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<p className="mt-2 text-sm text-muted-foreground font-bold">
										{t("formats.selected", { label: selectedFormat.label })}
									</p>
								</CardContent>
							</Card>
						)}

						{/* 3. Process Button */}
						{videoFiles.length > 1 && (
							<div className="flex justify-center">
								<Button
									onClick={stitchVideos}
									size="lg"
									variant={isLoaded && !isProcessing ? "default" : "outline"}
									disabled={!isLoaded || videoFiles.length < 2 || isProcessing}
									className="px-8 py-4 text-lg"
								>
									{isProcessing ? (
										<>
											<Loader className="w-5 h-5 me-3 animate-spin" aria-hidden="true" />
											{t("convert.stitchingVideos")}
										</>
									) : (
										<>
											<Play className="w-5 h-5 me-3" aria-hidden="true" />
											{t("convert.stitchButton", { count: videoFiles.length })}
										</>
									)}
								</Button>
							</div>
						)}

						{/* Progress Bar */}
						{isProcessing && (
							<Card className="mb-6 p-3 sm:p-6">
								<div className="flex justify-between items-center mb-4">
									<Badge variant="secondary" className="font-black text-lg">
										{currentPhase === 'analysis' && t("progress.analyzing")}
										{currentPhase === 'normalization' && t("progress.normalizing")}
										{currentPhase === 'concatenation' && t("progress.stitching")}
									</Badge>
									<span className="text-lg font-black">{Math.round(progress * 100)}%</span>
								</div>
								<Progress
									value={Math.round(progress * 100)}
									className="mb-4"
									aria-label={t("progress.processingLabel", { percent: Math.round(progress * 100) })}
								/>
								{currentFileProcessing && (
									<div className="text-sm font-bold text-center text-muted-foreground mb-2">
										{t("progress.processingFile", { name: currentFileProcessing })}
									</div>
								)}
								{estimatedTimeRemaining && (
									<div className="text-sm font-bold text-center text-muted-foreground">
										{estimatedTimeRemaining}
									</div>
								)}
							</Card>
						)}

						{/* 4. Download Section */}
						<Card className="p-3 sm:p-6">
							<CardHeader className="p-0 mb-4">
								<CardTitle className="flex items-center gap-3">
									<Download className="w-6 h-6" aria-hidden="true" />
									{t("steps.downloadStitched")}
								</CardTitle>
							</CardHeader>
							<CardContent className="p-0">
								{outputURL ? (
									<div className="space-y-6">
										{/* Stitching Results */}
										<div className="p-4">
											<h4 className="font-semibold mb-3">{t("download.stitchingResults")}</h4>
											<div className="grid grid-cols-2 gap-4 text-sm">
												<div>
													<span className="text-muted-foreground">{t("download.inputVideos")}</span>
													<div className="font-bold">{t("download.inputVideosValue", { count: videoFiles.length })}</div>
												</div>
												<div>
													<span className="text-muted-foreground">{t("download.totalInputSize")}</span>
													<div className="font-bold">{(totalInputSize / 1024 / 1024).toFixed(2)} MB</div>
												</div>
												<div>
													<span className="text-muted-foreground">{t("download.outputSize")}</span>
													<div className="font-bold">{(outputFileSize / 1024 / 1024).toFixed(2)} MB</div>
												</div>
												<div>
													<span className="text-muted-foreground">{t("download.format")}</span>
													<div className="font-bold">{selectedFormat.ext.toUpperCase()}</div>
												</div>
											</div>
										</div>

										{/* Video Preview */}
										<div>
											<h4 className="text-md font-bold mb-3">{t("download.videoPreview")}</h4>
											<VideoPlayer
												videoUrl={outputURL}
												fileName={`stitched.${selectedFormat.ext}`}
											/>
										</div>

										{/* Download Button */}
										<div className="flex flex-col items-center justify-center text-center space-y-4 pt-4 border-t">
											<Badge variant="secondary" className="text-lg font-black px-4 py-2">
												{t("download.completed")}
											</Badge>
											<Button asChild size="lg" className="px-6 py-3">
												<a
													href={outputURL}
													download={`stitched.${selectedFormat.ext}`}
													data-download-button
													aria-label={t("download.downloadLabel", { format: selectedFormat.ext.toUpperCase() })}
													onClick={() => announce(t("download.downloadStarted", { ext: selectedFormat.ext }))}
												>
													<Download className="w-5 h-5 me-3" aria-hidden="true" />
													{t("download.downloadFile", { ext: selectedFormat.ext })}
												</a>
											</Button>
										</div>
									</div>
								) : (
									<div className="text-center min-h-[12rem] flex items-center justify-center">
										{isProcessing ? (
											<p className="text-primary font-bold text-lg">
												{t("download.processingMessage")}
											</p>
										) : videoFiles.length < 2 ? (
											<p className="text-muted-foreground font-bold">
												{t("download.emptyNeedFiles")}
											</p>
										) : (
											<p className="text-muted-foreground font-bold">
												{t("download.emptyReady")}
											</p>
										)}
									</div>
								)}
							</CardContent>
						</Card>
					</div>

					{/* Information */}
					<Alert className="mt-8">
						<AlertTriangle className="h-4 w-4 text-green-600" aria-hidden="true" />
						<AlertDescription className="font-bold">
							{t("privacy")}
						</AlertDescription>
					</Alert>
				</div>
			</div>
		</ErrorBoundary>
	);
};

export default VideoStitcher;